import { english } from 'src/i18n/language';

// Small, pure helpers for showing and naming user files.

// 1536 → '1.5 KB'. One decimal below 10 of a unit, none above.
export function formatSize(bytes) {
  if (!bytes && bytes !== 0) return '';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let n = bytes;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i += 1;
  }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

// A longer run after the last dot is part of the name, not a type — e.g.
// 'Letter from Dr. Smithson-Beaumont' has no extension.
const MAX_EXTENSION_LENGTH = 8;

// 'letter.final.pdf' → { stem: 'letter.final', ext: '.pdf' }. A leading dot
// ('.env'), a trailing dot, or no dot at all gives no extension.
//
// Used to truncate a long file name in the MIDDLE: the stem ellipsises and
// the extension stays visible, so 'very-long-referral-le….pdf' still says
// what kind of file it is.
export function splitExtension(name) {
  const s = String(name ?? '');
  const dot = s.lastIndexOf('.');
  const ext = s.slice(dot);
  if (dot <= 0 || ext.length < 2 || ext.length > MAX_EXTENSION_LENGTH + 1)
    return { stem: s, ext: '' };
  return { stem: s.slice(0, dot), ext };
}

// The first of 'name.ext', 'name (2).ext', 'name (3).ext', … not in `taken`,
// compared case-insensitively (one storage backend is case-insensitive, and
// two names differing only in case read as the same file anyway). A name that
// already ends in ' (n)' counts on from its base, so a second 'a (2).pdf'
// becomes 'a (3).pdf' rather than 'a (2) (2).pdf'.
export function nextFreeName(name, taken) {
  const used = new Set([...(taken || [])].map((t) => String(t).toLowerCase()));
  if (!used.has(String(name).toLowerCase())) return name;
  const { stem, ext } = splitExtension(name);
  const base = stem.replace(/ \(\d+\)$/, '');
  for (let n = 2; ; n += 1) {
    const candidate = `${base} (${n})${ext}`;
    if (!used.has(candidate.toLowerCase())) return candidate;
  }
}

// 'just now', '5 min ago', '2 h ago' — how long since `then` (ms since epoch),
// in the caller's language (`t`; English when omitted).
export function sinceLabel(then, now = Date.now(), t = english) {
  const minutes = Math.floor((now - then) / 60000);
  if (!(minutes >= 1)) return t('just now');
  if (minutes < 60) return t('{{count}} min ago', { count: minutes });
  return t('{{count}} h ago', { count: Math.floor(minutes / 60) });
}
